import * as React from "react";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { Action } from "redux";

import { WritableReferralFormUnconnected } from "@teamprevent/common/questionnaires/components/referralForm/WritableReferralForm";
import { QuestionnaireConfig, CustomQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { updateQuestion } from "@teamprevent/common/questionnaires/actions";
import { AnsweredQuestion } from "@teamprevent/common/model";
import {
    REFERRAL_QUESTIONNAIRE_KEY,
    employeeDetailSectionId,
    furtherDetailsSectionId,
    lineManagerHrSectionId,
    selfReferralNo,
    selfReferralQuestion
} from "@teamprevent/common/questionnaires/components/referralForm/ReferralForm";
import * as questionIds from "@teamprevent/common/questionnaires/questionnaireConfig/referral/questionIds";

import { useDispatch } from "@teamprevent/admin-redux";

import { routeIdSelector as employeeRecordIdSelector, employeeRecordSelector } from "../../crud/employeeRecord/selectors";
import { routeIdSelector as customerIdSelector } from "../../crud/customer/selectors";
import { loadOrgStructures } from "../../crud/orgStructure";
import { ApplicationState } from "../../applicationState";
import { selectAndMergeReferralQuestionnaireConfig } from "../selectors";
import { EmployeeRecord } from "../../crud/employeeRecord";

import { loadQuestionnaireConfig } from "../../questionnaireConfig/actions";
import { loadHrUsers, loadLineManagers } from "../actions";

import { push } from "redux-little-router";

interface StateProps {
    customerId: string;
    employeeRecordId: string;
    employeeRecord: EmployeeRecord;
    questionnaireConfig: QuestionnaireConfig;
}

interface DispatchProps {
    loadOrgStructures: (customerId: string, parentOrgStructureId?: string) => void;
    goToOhCase: (customerId: string, employeeId: string, ohCaseId: string) => void;
    updateAnswer: (id: string, question: AnsweredQuestion, questionnaireKey: string, sectionId: string) => void;
}

type Props = StateProps & DispatchProps;

const ReferralFormUnconnected: React.FunctionComponent<Props> = (props) => {
    const dispatch = useDispatch();

    React.useEffect(() => {
        dispatch(loadQuestionnaireConfig({ customerId: props.customerId, questionnaireType: "referral" }));
        dispatch(loadLineManagers({ customerId: props.customerId }));
        dispatch(loadHrUsers({ customerId: props.customerId }));
    }, [dispatch, props.customerId]);

    React.useEffect(() => {
        (async () => {
            await props.loadOrgStructures(props.customerId);
            props.updateAnswer(
                questionIds.SelfReferral,
                {
                    question: selfReferralQuestion.questionText,
                    sectionId: lineManagerHrSectionId,
                    id: selfReferralQuestion.id,
                    answer: selfReferralNo,
                    isValid: true
                },
                REFERRAL_QUESTIONNAIRE_KEY,
                lineManagerHrSectionId);
            setAnswer(employeeDetailSectionId, questionIds.ReferredTitle, props.employeeRecord.title);
            setAnswer(employeeDetailSectionId, questionIds.ReferredFirstName, props.employeeRecord.firstName);
            setAnswer(employeeDetailSectionId, questionIds.ReferredLastName, props.employeeRecord.lastName);
            setAnswer(
                employeeDetailSectionId,
                questionIds.ReferredDob,
                props.employeeRecord.dateOfBirth && props.employeeRecord.dateOfBirth.format("YYYY-MM-DD")
            );
            setAnswer(employeeDetailSectionId, questionIds.ReferredGender, props.employeeRecord.gender);
            setAnswer(employeeDetailSectionId, questionIds.EmployeeId, props.employeeRecord.secondaryReference);
            setAnswer(employeeDetailSectionId, questionIds.JobTitle, props.employeeRecord.jobTitle);
            setAnswer(employeeDetailSectionId, questionIds.AddressLine1, props.employeeRecord.addressLine1);
            setAnswer(employeeDetailSectionId, questionIds.AddressLine2, props.employeeRecord.addressLine2);
            setAnswer(employeeDetailSectionId, questionIds.AddressLine3, props.employeeRecord.addressLine3);
            setAnswer(employeeDetailSectionId, questionIds.City, props.employeeRecord.city);
            setAnswer(employeeDetailSectionId, questionIds.County, props.employeeRecord.county);
            setAnswer(employeeDetailSectionId, questionIds.Postcode, props.employeeRecord.postCode);
            setAnswer(employeeDetailSectionId, questionIds.PersonalEmailAddress, props.employeeRecord.personalEmailAddress);
            setAnswer(employeeDetailSectionId, questionIds.ConfirmPersonalEmailAddress, props.employeeRecord.personalEmailAddress);
            setAnswer(employeeDetailSectionId, questionIds.MobilePhone, props.employeeRecord.mobilePhoneNumber);
            setAnswer(employeeDetailSectionId, questionIds.HomePhone, props.employeeRecord.homePhoneNumber);
            setAnswer(employeeDetailSectionId, questionIds.WorkPhone, props.employeeRecord.workPhoneNumber);
            setAnswer(
                furtherDetailsSectionId,
                questionIds.OrgLevel1Id,
                props.employeeRecord.orgStructureOne && props.employeeRecord.orgStructureOne.id,
                questionIds.OrgLevel1Name);
            setAnswer(
                furtherDetailsSectionId,
                questionIds.OrgLevel2Id,
                props.employeeRecord.orgStructureTwo && props.employeeRecord.orgStructureTwo.id,
                questionIds.OrgLevel2Name);
            setAnswer(
                furtherDetailsSectionId,
                questionIds.OrgLevel3Id,
                props.employeeRecord.orgStructureThree && props.employeeRecord.orgStructureThree.id,
                questionIds.OrgLevel3Name);
            setAnswer(
                furtherDetailsSectionId,
                questionIds.OrgLevel4Id,
                props.employeeRecord.orgStructureFour && props.employeeRecord.orgStructureFour.id,
                questionIds.OrgLevel4Name);
        })();
    }, [props.questionnaireConfig]);

    const setAnswer = (sectionId: string, questionId: string, answer: string, questionConfigId?: string) => {
        if (!props.questionnaireConfig) {
            return;
        }
        const questionConfig =
            props.questionnaireConfig.questions.find(q => q.id === (questionConfigId || questionId)) as CustomQuestionConfig;
        props.updateAnswer(
            questionId,
            { question: questionConfig.questionText, sectionId, id: questionId, answer, isValid: true },
            REFERRAL_QUESTIONNAIRE_KEY,
            sectionId);
    };

    const goToOhCase = (ohCaseId: string) => props.goToOhCase(props.customerId, props.employeeRecordId, ohCaseId);

    return (
        <WritableReferralFormUnconnected
            questionnaireConfig={props.questionnaireConfig}
            api={`/api/customer/${props.customerId}/employeeRecord/${props.employeeRecordId}/referralCase`}
            afterSubmit={goToOhCase}
            loadOrgStructure={parentOrgStructureId => props.loadOrgStructures(props.customerId, parentOrgStructureId)}
            documentBaseApi={`/api/referral/customer/${props.customerId}`}
        />
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    customerId: customerIdSelector(state),
    employeeRecordId: employeeRecordIdSelector(state),
    employeeRecord: employeeRecordSelector(state),
    questionnaireConfig: selectAndMergeReferralQuestionnaireConfig(state)
});

const mapDispatchToProps = (dispatch: ThunkDispatch<ApplicationState, undefined, Action>): DispatchProps => {
    return {
        loadOrgStructures: (customerId, parentOrgStructureId) => dispatch(loadOrgStructures({ customerId, parentOrgStructureId })),
        updateAnswer: (id, question, questionnaireKey, sectionId) => dispatch(updateQuestion({ id, question, questionnaireKey, sectionId })),
        goToOhCase: (customerId, employeeId, ohCaseId) => dispatch(push(`/customers/${customerId}/employeeRecords/${employeeId}/ohCases/${ohCaseId}`))
    };
};

export const ReferralForm = connect(mapStateToProps, mapDispatchToProps)(ReferralFormUnconnected);
